import React, { useEffect, useState, useRef } from "react";
import { Input, Modal, Form, Rate, Tooltip, Checkbox, Alert } from "antd";
import { BounceLoader } from "react-spinners";
import http from "../../../../services/http";
import { Close } from "@mui/icons-material";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import zxcvbn from "zxcvbn";
import Cookie from "js-cookie";
import {
  getColorForStrength,
  getStrengthLabel,
  negativeStyle,
  positiveStyle,
} from "../../../../utils";

const checkPasswordStrength = (password) => {
  const result = zxcvbn(password);
  const score = result.score;
  const feedback = result.feedback.suggestions.join(" ");
  return { score, feedback };
};

const checkPasswordAgainstBreachDatabase = async (password) => {
  const sha1 = require("crypto").createHash("sha1");
  sha1.update(password);
  const hash = sha1.digest("hex").toUpperCase();
  const prefix = hash.substring(0, 5);
  const suffix = hash.substring(5);

  const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`);
  const text = await response.text();
  const hashes = text.split("\n").map((line) => line.split(":")[0]);

  const isBreached = hashes.some((h) => h === suffix);
  return isBreached;
};

const ChangePassword = ({ preview, previewHandler }) => {
  const [isLoading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [inputError, setinputError] = useState({
    passStrength: 0,
    notEmail: true,
    length: false,
    specialChar: false,
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [form] = Form.useForm();

  const newPassChangeHandler = (e) => {
    setPasswordError("");
    const value = e.target.value;
    const temp = { ...inputError };
    const { score } = checkPasswordStrength(value);
    temp.passStrength = score;
    temp.notEmail = !value.includes(Cookie.get("email"));
    temp.length = value.length >= 8;
    temp.specialChar = /[^a-zA-Z]/.test(value);
    setinputError(temp);
  };
  const showPasswordHandler = (field) => {
    setShowPassword((prevState) => {
      return {
        ...prevState,
        [field]: !prevState[field],
      };
    });
  };
  const submitHandler = async (values) => {
    if (values.old_password === values.new_password) {
      setPasswordError("New password cannot be same as old password");
      return;
    }
    if (
      inputError.passStrength < 3 ||
      !inputError.notEmail ||
      !inputError.length ||
      !inputError.specialChar
    ) {
      setPasswordError("Please ensure your password meets the requirements");
      return;
    }
    const isBreached = await checkPasswordAgainstBreachDatabase(values.new_password);
    if (isBreached) {
      setPasswordError(
        "This password has been found in a database breach. Please choose another password."
      );
      return;
    }
    setLoading(true);
    const payload = {
      ...values,
      type: "reset",
    };
    http
      .put("change-password", payload)
      .then(() => {
        previewHandler();
        toast.success("Password changed successfully");
      })
      .catch((err) => {
        const errorObj = err.response.data ?? {};
        Object.keys(errorObj).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: [errorObj[key][key]],
            },
          ]);
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    form.resetFields();
    setPassword("");
    setPasswordError("");
    setShowPassword({
      old: false,
      new: false,
      confirm: false,
    });
    setinputError({
      passStrength: 0,
      notEmail: true,
      length: false,
      specialChar: false,
    });
  }, [preview]);

  return (
    <Modal
      visible={preview}
      onCancel={previewHandler}
      closable={false}
      footer={null}
      className="change-password-modal"
    >
      <button className="close-btn" onClick={previewHandler}>
        <Close />
      </button>
      <h2>Change Password</h2>
      <p>Change your password below to keep your credentials safe and up to date.</p>
      {passwordError && <Alert type="error" message={passwordError} />}
      <Form form={form} onFinish={submitHandler}>
        <label className="field-label" htmlFor="oldPassowrd">
          Old Password
        </label>
        <Form.Item
          name="old_password"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Old password is required",
            },
          ]}
        >
          <Input
            type={showPassword.old ? "text" : "password"}
            id="oldPassowrd"
            suffix={
              <span
                style={{ cursor: "pointer", fontSize: 16 }}
                onClick={() => showPasswordHandler("old")}
              >
                {showPassword.old ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </span>
            }
          />
        </Form.Item>
        <label className="field-label" htmlFor="newPassowrd">
          New Password
          <Tooltip
            title="To confirm with our strong password policy, you are required to
                use a sufficiently strong password. Password must be more than 7
                characters"
          >
            <span>
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        </label>
        {/* <PasswordStrengthBar className="strength-bar" password={password} /> */}
        <Form.Item name="new_password">
          <Input
            id="newPassowrd"
            type={showPassword.new ? "text" : "password"}
            onChange={newPassChangeHandler}
            suffix={
              <>
                {password.length === 0 ? (
                  <></>
                ) : passwordError >= 3 ? (
                  <CheckCircleOutlined style={{ color: "rgb(37, 194, 129)" }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "rgb(239, 72, 54)" }} />
                )}
                <span
                  style={{ cursor: "pointer", fontSize: 16 }}
                  onClick={() => showPasswordHandler("new")}
                >
                  {showPassword.new ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </span>
              </>
            }
          />
        </Form.Item>{" "}
        <ul className="password-strength-box">
          <li style={{ color: getColorForStrength(inputError.passStrength) }}>
            <span className="thumb">
              {inputError.passStrength >= 3 ? <CheckOutlined /> : <CloseOutlined />}
            </span>
            Password strength: {getStrengthLabel(inputError.passStrength)}
          </li>
          <li style={inputError.notEmail ? positiveStyle : negativeStyle}>
            <span className="thumb">
              {inputError.notEmail ? <CheckOutlined /> : <CloseOutlined />}
            </span>
            Must not contain your email address
          </li>
          <li style={inputError.length ? positiveStyle : negativeStyle}>
            <span className="thumb">
              {inputError.length ? <CheckOutlined /> : <CloseOutlined />}
            </span>
            At least 8 characters
          </li>
          <li style={inputError.specialChar ? positiveStyle : negativeStyle}>
            <span className="thumb">
              {inputError.specialChar ? <CheckOutlined /> : <CloseOutlined />}
            </span>
            Must include a number or symbol
          </li>
        </ul>
        <label className="field-label" htmlFor="confirmPassowrd">
          Confirm New Password
        </label>
        <Form.Item
          name="confirm_password"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Confirm password is required",
            },
            {
              min: 8,
              message: "Minimum 8 characters required",
            },
            {
              validator: (_, value) => {
                if (!value || value.length < 8) {
                  return Promise.resolve();
                }
                if (value === form.getFieldValue("new_password")) {
                  return Promise.resolve();
                } else {
                  return Promise.reject("Passwords do not match");
                }
              },
            },
          ]}
        >
          <Input
            id="confirmPassowrd"
            type={showPassword.confirm ? "text" : "password"}
            suffix={
              <span
                style={{ cursor: "pointer", fontSize: 16 }}
                onClick={() => showPasswordHandler("confirm")}
              >
                {showPassword.confirm ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </span>
            }
          />
        </Form.Item>
        <div className="action-box">
          <button type="button" onClick={previewHandler}>
            Cancel
          </button>
          <button type="submit" className="submit" disabled={isLoading}>
            {isLoading ? <BounceLoader color="#fff" size={15} /> : "Submit"}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

export default ChangePassword;
